import type { UiTemplateSection, Variable, VariableType } from "./types";
import { isJsonPointer } from "./subanalysis";

export type StepVariableSourceVerification = "ready" | "unverified";
export type StepVariableBindingReadiness = "ready" | "incomplete";

export type StepVariableSource = {
  id: string;
  stepId: string;
  direction: "input" | "output";
  label: string;
  type: VariableType;
  variable: Variable;
  readiness: StepVariableSourceVerification;
  requiresProjection: boolean;
};

export type StepVariableSourceGroup = {
  stepId: string;
  stepName: string;
  order: number;
  inputs: StepVariableSource[];
  outputs: StepVariableSource[];
};

const systemRoles = new Set(["subanalysis_call", "subanalysis_source"]);
const visibleRoles = new Set([
  "ordinary",
  "step_binding",
  "subanalysis_call",
  "subanalysis_source",
  "bound_input",
]);

export function isSystemSelector(variable: Pick<Variable, "variable_role">) {
  return systemRoles.has(variable.variable_role);
}

export function isOutputDefinition(
  variable: Pick<Variable, "variable_kind" | "variable_role">,
) {
  return (
    variable.variable_kind === "output" &&
    (variable.variable_role === "ordinary" || variable.variable_role == null)
  );
}

export function isRuntimeInputVariable(
  variable: Pick<Variable, "variable_kind" | "variable_role">,
) {
  return (
    variable.variable_kind !== "output" &&
    variable.variable_kind !== "runtime" &&
    (variable.variable_role === "ordinary" || variable.variable_role == null)
  );
}

export function isEditableVariableRow(variable: Variable) {
  return variable.variable_kind === "input" || isOutputDefinition(variable);
}

export function getVariableEditPolicy(variable: Variable) {
  const protectedSelector = isSystemSelector(variable);
  return {
    canRename: !protectedSelector,
    canChangeType: !protectedSelector,
    canDelete: !protectedSelector,
    canChangeValue: variable.variable_kind === "input",
  };
}

function byOrderAndId(a: Variable, b: Variable) {
  return (
    a.variable_order - b.variable_order ||
    a.variable_id.localeCompare(b.variable_id)
  );
}

/** Visible step rows, including protected selectors and bound children. */
export function groupStepVariables(variables: readonly Variable[]) {
  const visible = variables.filter(
    (variable) =>
      variable.variable_is_visible &&
      visibleRoles.has(variable.variable_role ?? "ordinary"),
  );
  return {
    inputs: visible
      .filter(
        (variable) =>
          variable.variable_kind !== "output" &&
          variable.variable_kind !== "runtime",
      )
      .sort(byOrderAndId),
    outputs: visible.filter(isOutputDefinition).sort(byOrderAndId),
  };
}

function isReadableSource(variable: Variable) {
  return (
    variable.variable_is_visible &&
    (variable.variable_role === "ordinary" ||
      (variable.variable_role === "step_binding" &&
        variable.binding_kind === "step_variable")) &&
    (variable.variable_kind === "input" || isOutputDefinition(variable))
  );
}

/** Type equality is guaranteed; JSON projections need separate validation. */
export function isCompatibleSourceType(
  sourceType: VariableType,
  targetType?: VariableType,
) {
  return (
    !targetType ||
    sourceType === targetType ||
    (sourceType === "string" && targetType === "text") ||
    (sourceType === "text" && targetType === "string")
  );
}

function projectable(type: VariableType) {
  return type === "json" || type === "array";
}

/** Dynamic projections remain incomplete until their result type is declared and verified. */
export function getStepVariableBindingReadiness(
  source: StepVariableSource,
  bindingPath?: string | null,
  targetType?: VariableType,
): StepVariableBindingReadiness {
  if (bindingPath != null && !isJsonPointer(bindingPath)) return "incomplete";
  if (bindingPath) return "incomplete";
  if (!isCompatibleSourceType(source.type, targetType)) return "incomplete";
  return source.readiness === "ready" ? "ready" : "incomplete";
}

function resolveSourceReadiness(
  variable: Variable,
  owner: UiTemplateSection,
  definitions: ReadonlyMap<
    string,
    { variable: Variable; step: UiTemplateSection }
  >,
  visited: Set<string>,
): StepVariableSourceVerification | null {
  if (visited.has(variable.variable_id) || !isReadableSource(variable))
    return null;
  if (variable.variable_role !== "step_binding") {
    return variable.variable_kind === "input" &&
      variable.variable_default == null
      ? "unverified"
      : "ready";
  }
  const sourceId = variable.binding_source_variable_id;
  if (!sourceId) return null;
  const source = definitions.get(sourceId);
  if (!source || source.step.order >= owner.order) return null;
  const nextVisited = new Set(visited);
  nextVisited.add(variable.variable_id);
  const upstream = resolveSourceReadiness(
    source.variable,
    source.step,
    definitions,
    nextVisited,
  );
  if (!upstream) return null;
  const path = variable.binding_path;
  if (path != null && !isJsonPointer(path)) return null;
  if (path)
    return projectable(source.variable.variable_type) ? "unverified" : null;
  return isCompatibleSourceType(
    source.variable.variable_type,
    variable.variable_type,
  )
    ? upstream
    : null;
}

export function getStepVariableSources(
  steps: readonly UiTemplateSection[],
  consumerStepId: string,
  selectedSourceStepId?: string,
  targetType?: VariableType,
): StepVariableSourceGroup[] {
  const consumer = steps.find((step) => step.id === consumerStepId);
  if (!consumer) return [];
  const definitions = new Map(
    steps.flatMap((step) =>
      step.variables.map(
        (variable) => [variable.variable_id, { variable, step }] as const,
      ),
    ),
  );
  return steps
    .filter(
      (step) =>
        step.order < consumer.order &&
        (!selectedSourceStepId || step.id === selectedSourceStepId),
    )
    .sort((a, b) => a.order - b.order || a.id.localeCompare(b.id))
    .map((step) => {
      const candidates = step.variables
        .filter(
          (variable) =>
            isReadableSource(variable) &&
            (isCompatibleSourceType(variable.variable_type, targetType) ||
              projectable(variable.variable_type)),
        )
        .sort(byOrderAndId)
        .flatMap((variable): StepVariableSource[] => {
          const readiness = resolveSourceReadiness(
            variable,
            step,
            definitions,
            new Set(),
          );
          if (!readiness) return [];
          const requiresProjection = !isCompatibleSourceType(
            variable.variable_type,
            targetType,
          );
          return [
            {
              id: variable.variable_id,
              stepId: step.id,
              direction: variable.variable_kind as "input" | "output",
              label: `${step.name} / ${variable.variable_name_frontend || variable.variable_name} (${variable.variable_type}) [${variable.variable_id}]`,
              type: variable.variable_type,
              variable,
              readiness: requiresProjection ? "unverified" : readiness,
              requiresProjection,
            },
          ];
        });
      return {
        stepId: step.id,
        stepName: step.name,
        order: step.order,
        inputs: candidates.filter(
          (candidate) => candidate.direction === "input",
        ),
        outputs: candidates.filter(
          (candidate) => candidate.direction === "output",
        ),
      };
    });
}

export function displayStepVariableSource(
  groups: readonly StepVariableSourceGroup[],
  variableId: string | null,
) {
  if (!variableId) return "Select a variable";
  return (
    groups
      .flatMap((group) => [...group.inputs, ...group.outputs])
      .find((source) => source.id === variableId)?.label ??
    `Missing variable (${variableId})`
  );
}
